/**
 * SelectField — a value picker on the shared Radix DropdownMenu, so it gets
 * the app's menu material and portal positioning instead of the native
 * (OS-themed) <select> popup.
 *
 * Two triggers: `field` is the full-width glass field of the agent editor;
 * `value` is the Settings pocket's value text plus a 12px chevron at a row's
 * end. Options carrying a `group` render under non-interactive headers at
 * each group boundary; a list with no groups renders as a flat menu.
 */
import { Fragment, useEffect, useRef, useState } from "react";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/cn";
import { Icons } from "./icons";
import { useUiScale } from "./ui-scale-context";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "./dropdown-menu";

export interface SelectFieldOption {
  value: string;
  label: string;
  /** Header rendered above the first option of each run of this group. */
  group?: string;
  /** Muted qualifier beside the group header (e.g. "subscription"). */
  groupHint?: string;
  /** Muted text on the option's right edge (e.g. "Free"). */
  trailing?: string;
  /** Muted line under the label saying what the option is for. */
  description?: string;
  /** A second muted line under the description (e.g. measured accuracy). */
  detail?: string;
}

const triggerVariants = cva("", {
  variants: {
    variant: {
      field:
        "flex h-[34px] w-full items-center justify-between gap-2 rounded-control border border-glass-stroke-light bg-glass-fill-heavy px-appkit-sm text-left text-appkit-mini text-primary backdrop-blur-glass-sm focus:border-accent focus:outline-none data-[state=open]:border-accent",
      value:
        "relative left-2 inline-flex h-7 min-w-0 max-w-[min(280px,100%)] items-center gap-1 rounded-control-sm px-2 text-appkit-body text-secondary outline-none transition-colors hover:bg-[var(--color-interactive-hover)] hover:text-primary focus-visible:ring-2 focus-visible:ring-accent data-[state=open]:bg-[var(--color-interactive-hover)] data-[state=open]:text-primary",
    },
  },
  defaultVariants: { variant: "field" },
});

const contentVariants = cva("scrollbar-none !overflow-y-auto", {
  variants: {
    variant: {
      field:
        "max-h-[280px] w-[var(--radix-dropdown-menu-trigger-width)] min-w-[8rem]",
      value: "max-h-[320px] min-w-[170px] max-w-[min(360px,calc(100vw-2rem))]",
    },
  },
  defaultVariants: { variant: "field" },
});

const itemVariants = cva("justify-between", {
  variants: {
    variant: { field: "text-appkit-mini", value: "text-appkit-body" },
  },
  defaultVariants: { variant: "field" },
});

interface SelectFieldProps {
  value: string;
  options: ReadonlyArray<SelectFieldOption>;
  onChange: (value: string) => void;
  testId: string;
  /** Lets a form label point at the trigger; Radix generates one otherwise. */
  id?: string;
  /** Marks the trigger invalid in a form. */
  invalid?: boolean;
  /** The id of the element describing the trigger, such as its error. */
  describedBy?: string;
  disabled?: boolean;
  placeholder?: string;
  /** `value`: the Settings pocket's value text at a row's end. */
  variant?: "field" | "value";
  /** Trigger text when it should differ from the chosen option's label. */
  valueLabel?: string;
  /** Accessible name of the trigger (the row it belongs to). */
  ariaLabel?: string;
}

export function SelectField({
  value,
  options,
  onChange,
  testId,
  id,
  invalid,
  describedBy,
  disabled = false,
  placeholder,
  variant = "field",
  valueLabel,
  ariaLabel,
}: SelectFieldProps) {
  const scale = useUiScale();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);
  const label = valueLabel ?? selected?.label ?? placeholder ?? value;

  useEffect(() => {
    if (!open) return;
    const dismiss = () => setOpen(false);
    window.addEventListener("blur", dismiss);
    return () => window.removeEventListener("blur", dismiss);
  }, [open]);

  return (
    <DropdownMenu modal={false} open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          ref={triggerRef}
          {...(id ? { id } : {})}
          type="button"
          aria-invalid={invalid}
          aria-describedby={describedBy}
          disabled={disabled}
          aria-label={ariaLabel ? `${ariaLabel}: ${label}` : undefined}
          className={cn(
            triggerVariants({ variant }),
            disabled && "cursor-not-allowed opacity-50",
          )}
          data-testid={testId}
        >
          <span className="min-w-0 truncate" title={label}>
            {label}
          </span>
          <Icons.ChevronDown className="h-3 w-3 shrink-0 text-muted" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={variant === "value" ? "end" : "start"}
        sideOffset={4 * scale}
        className={contentVariants({ variant })}
        style={
          scale > 1
            ? {
                zoom: scale,
                width:
                  variant === "field"
                    ? `calc(var(--radix-dropdown-menu-trigger-width) / ${scale})`
                    : undefined,
              }
            : undefined
        }
        onPointerDownOutside={(event) => {
          const panel =
            triggerRef.current?.closest<HTMLElement>('[role="dialog"]');
          const target = event.detail.originalEvent.target;
          if (panel && target instanceof Node && panel.contains(target)) {
            panel.focus({ preventScroll: true });
          }
        }}
      >
        {options.map((opt, index) => {
          const header =
            opt.group && opt.group !== options[index - 1]?.group
              ? opt.group
              : null;
          return (
            <Fragment key={opt.value}>
              {header && (
                <DropdownMenuLabel data-testid={`${testId}-group-${header}`}>
                  <span className="font-semibold uppercase tracking-wide">
                    {header}
                  </span>
                  {opt.groupHint && (
                    <span className="text-subtle">{opt.groupHint}</span>
                  )}
                </DropdownMenuLabel>
              )}
              <DropdownMenuItem
                onSelect={() => onChange(opt.value)}
                data-testid={`${testId}-option-${opt.value}`}
                data-selected={opt.value === value ? "true" : undefined}
                role="menuitemradio"
                aria-checked={opt.value === value}
                className={cn(
                  itemVariants({ variant }),
                  opt.value === value && "bg-btn-ghost-fill text-primary",
                )}
              >
                {/* The label only flexes when a trailing marker or a
                    description comes with it, so a plain list keeps its
                    original layout. */}
                {opt.description ? (
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate">{opt.label}</span>
                    <span className="text-appkit-mini text-muted">
                      {opt.description}
                    </span>
                    {opt.detail && (
                      <span className="text-appkit-mini text-subtle">
                        {opt.detail}
                      </span>
                    )}
                  </span>
                ) : (
                  <span
                    className={cn("truncate", opt.trailing && "min-w-0 flex-1")}
                  >
                    {opt.label}
                  </span>
                )}
                {opt.trailing && (
                  <span className="shrink-0 text-muted">{opt.trailing}</span>
                )}
                {opt.value === value && (
                  <Icons.Check className="h-3.5 w-3.5 shrink-0" aria-hidden />
                )}
              </DropdownMenuItem>
            </Fragment>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
